import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BookOpen, Plus, MoreVertical, Pencil, Trash2, Search, Upload, Library, ClipboardList, Clock, Copy, FileDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyAssign = { name: '', code: '', catalog_subject_id: '', class_id: '', section_id: '', teacher_id: '', hours_per_week: '', subject_type: 'theory', uv_group: '', uv_order: '', max_note: '', coefficient: '' };
const emptyCatalog = { name: '', code: '' };

// Localized staff name resolver (4211 accounts carry name_en/name_fr/name_pt)
const staffName = (s, lang) => {
  if (!s) return '';
  if (lang === 'fr') return s.name_fr || s.name_en || s.name || s.name_pt || '';
  if (lang === 'pt') return s.name_pt || s.name_en || s.name || s.name_fr || '';
  return s.name_en || s.name || s.name_fr || s.name_pt || '';
};

export default function SubjectsPage() {
  const { canAdmin, currentOrg } = useAuth();
  const { t, lang } = useT();
  const orgId = useOrgId();
  const { selectedYearId, selectedYear } = useAcademicYear();
  const [tab, setTab] = useState('catalog');

  // ---------- Catalog state ----------
  const [catalog, setCatalog] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catEditing, setCatEditing] = useState(null);
  const [catForm, setCatForm] = useState(emptyCatalog);
  const [catNextCode, setCatNextCode] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catDeleteId, setCatDeleteId] = useState(null);

  // catalog import
  const catFileRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  // ---------- Assignment state ----------
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterSections, setFilterSections] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAssign);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // ---------- Export Subject Class Lists (one page per subject) ----------
  const [exportingSubjects, setExportingSubjects] = useState(false);

  const buildSubjectListsHtml = (subs, students) => {
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const logoUrl = currentOrg?.logo_url
      ? (currentOrg.logo_url.startsWith('http') ? currentOrg.logo_url : `${process.env.REACT_APP_BACKEND_URL}${currentOrg.logo_url}`)
      : '';
    const coaUrl = `${window.location.origin}/gb-coat-of-arms.png`;
    const schoolName = currentOrg?.name || 'Escola Libanesa Internacional – ELI';
    const yearName = selectedYear?.name || '';
    const cls = classes.find((c) => c.id === filterClass);
    const className = (cls?.name || '').toUpperCase();
    const sec = (filterSections || []).find((s) => s.id === filterSection) || sections.find((s) => s.id === filterSection);
    const sectionName = (sec?.name || '').toUpperCase();
    const turma = `${className}${sectionName ? ' ' + sectionName : ''}`;
    const N = subs.length;
    const studentRows = students.length
      ? students.map((s, i) => `<tr><td class="num">${i + 1}</td><td class="nome">${esc((s.name || '').toUpperCase())}</td><td class="score"></td></tr>`).join('')
      : '<tr><td colspan="3" style="text-align:center">Sem alunos</td></tr>';
    const pages = subs.map((s, idx) => {
      const maxg = (s.max_note === 0 || s.max_note) ? s.max_note : '—';
      return `<div class="page">
        <div class="shead">
          <div class="logos">${coaUrl ? `<img class="coa" src="${coaUrl}" alt="coa"/>` : ''}${logoUrl ? `<img class="logo" src="${logoUrl}" alt="logo"/>` : ''}</div>
          <div class="info">
            <div class="sch">${esc(schoolName)}</div>
            <div class="meta">Ano Escolar – <b>${esc(yearName) || '—'}</b></div>
            <div class="meta">Turma: <b>${esc(turma)}</b></div>
          </div>
        </div>
        <div class="subbar">
          <div><div class="subj">${esc(s.name)}</div><div class="tea">Professor(a): <b>${esc(s.teacher_name || '—')}</b></div></div>
          <div class="maxg">Nota Máxima: ${esc(maxg)}</div>
        </div>
        <div class="bodywrap">
          <table>
            <thead><tr><th class="num">Nº</th><th class="nome">Nome do Aluno</th><th class="score">Nota (Score)</th></tr></thead>
            <tbody>${studentRows}</tbody>
          </table>
        </div>
        <div class="sfoot"><span>${esc(s.name)}</span><span>Página ${idx + 1} de ${N}</span><span>${esc(turma)}</span></div>
      </div>`;
    }).join('');
    return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"/>
      <title>Listas por Disciplina - ${esc(turma)}</title>
      <style>
        @page { size: A4 portrait; margin: 14mm 14mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; }
        .page { min-height: 265mm; display: flex; flex-direction: column; }
        .page + .page { page-break-before: always; }
        .shead { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #000; padding-bottom: 8px; }
        .shead .logos { display: flex; align-items: center; gap: 10px; flex: none; }
        .shead .logos .coa { height: 52px; width: auto; object-fit: contain; }
        .shead .logos .logo { height: 64px; width: auto; object-fit: contain; }
        .shead .info { margin-left: auto; text-align: right; }
        .shead .info .sch { font-size: 18px; font-weight: 800; }
        .shead .info .meta { font-size: 12px; margin-top: 2px; }
        .subbar { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-top: 10px; }
        .subbar .subj { font-size: 20px; font-weight: 800; }
        .subbar .tea { font-size: 13px; margin-top: 2px; }
        .subbar .maxg { font-size: 14px; font-weight: 700; border: 1px solid #000; padding: 5px 12px; white-space: nowrap; }
        .bodywrap { flex: 1 1 auto; margin-top: 8px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th, td { border: 1px solid #000; padding: 6px 9px; }
        th { text-align: center; font-weight: 700; }
        th.nome, td.nome { text-align: left; }
        td.num, th.num { width: 44px; text-align: center; }
        td.score, th.score { width: 28%; }
        tr { page-break-inside: avoid; }
        thead { display: table-header-group; }
        .sfoot { margin-top: auto; border-top: 1px solid #000; padding-top: 6px; display: flex; justify-content: space-between; font-size: 11px; font-weight: 600; }
      </style></head>
      <body>${pages}</body></html>`;
  };

  const exportSubjectClassLists = async () => {
    if (!filterClass || !filterSection) {
      toast.error(t('subjects.exportNeedFilter', 'Select a class and section first'));
      return;
    }
    const assigned = subjects.filter((s) => s.class_id === filterClass && s.section_id === filterSection);
    const subs = [...assigned].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    if (subs.length === 0) {
      toast.error(t('subjects.exportNoSubjects', 'No subjects assigned to this class/section'));
      return;
    }
    setExportingSubjects(true);
    try {
      const res = await schoolApi.listStudents(orgId, {
        class_id: filterClass, section_id: filterSection, academic_year_id: selectedYearId,
        sort_by: 'name', sort_dir: 'asc', limit: 10000,
      });
      const students = res.students || [];
      const html = buildSubjectListsHtml(subs, students);
      const w = window.open('', '_blank');
      if (!w) { toast.error(t('common.popupBlocked', 'Popup blocked. Please allow popups to print.')); return; }
      w.document.open(); w.document.write(html); w.document.close(); w.focus();
      setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 450);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('subjects.exportFailed', 'Export failed'));
    } finally { setExportingSubjects(false); }
  };


  // searchable catalog-subject picker (assignment)
  const [subjSearch, setSubjSearch] = useState('');
  const [subjResults, setSubjResults] = useState([]);
  const [selectedSubjLabel, setSelectedSubjLabel] = useState('');

  // searchable teacher/staff picker (assignment)
  const [teacherSearch, setTeacherSearch] = useState('');
  const [teacherResults, setTeacherResults] = useState([]);
  const [selectedTeacherLabel, setSelectedTeacherLabel] = useState('');

  // assignment import
  const assignFileRef = useRef(null);
  const [assignImportOpen, setAssignImportOpen] = useState(false);
  const [assignFile, setAssignFile] = useState(null);
  const [assignPreview, setAssignPreview] = useState(null);
  const [assignImporting, setAssignImporting] = useState(false);

  // copy-subjects-to-section helper
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargetSection, setCopyTargetSection] = useState('');
  const [copying, setCopying] = useState(false);

  // ===================== Catalog loaders =====================
  const loadCatalog = useCallback(async () => {
    if (!orgId) { setCatalogLoading(false); return; }
    try {
      setCatalogLoading(true);
      const data = await schoolApi.listSubjectCatalog(orgId, { search: catalogSearch, limit: 1000 });
      setCatalog(data.catalog || []);
      setCatalogTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load catalog'); }
    finally { setCatalogLoading(false); }
  }, [orgId, catalogSearch]);

  useEffect(() => { const id = setTimeout(loadCatalog, 250); return () => clearTimeout(id); }, [loadCatalog]);

  const openCatCreate = async () => {
    setCatEditing(null); setCatForm(emptyCatalog); setCatDialogOpen(true);
    try { const r = await schoolApi.subjectCatalogNextCode(orgId); setCatNextCode(r.next_code); } catch { setCatNextCode(''); }
  };
  const openCatEdit = (c) => { setCatEditing(c); setCatForm({ name: c.name || '', code: c.code || '' }); setCatNextCode(c.code); setCatDialogOpen(true); };

  const saveCatalog = async () => {
    if (!catForm.name.trim()) { toast.error(t('common.required', 'Name is required')); return; }
    setCatSaving(true);
    try {
      if (catEditing) { await schoolApi.updateSubjectCatalog(catEditing.id, { name: catForm.name.trim(), code: catForm.code.trim() || null }); }
      else { await schoolApi.createSubjectCatalog({ organization_id: orgId, name: catForm.name.trim(), code: catForm.code.trim() || null }); }
      toast.success(t('common.saved', 'Saved')); setCatDialogOpen(false); loadCatalog();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setCatSaving(false); }
  };

  const confirmCatDelete = async () => {
    try { await schoolApi.deleteSubjectCatalog(catDeleteId); toast.success(t('common.deleted', 'Deleted')); setCatDeleteId(null); loadCatalog(); }
    catch (e) { toast.error('Failed'); }
  };

  // ----- Catalog import -----
  const onPickCatFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file); setImportPreview(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/school/subjects/catalog/import-excel/preview?organization_id=${orgId}`, fd);
      setImportPreview(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to preview file'); setImportFile(null); }
    if (catFileRef.current) catFileRef.current.value = '';
  };

  const runCatImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', importFile);
      const r = await axios.post(`${API}/school/subjects/catalog/import-excel?organization_id=${orgId}`, fd);
      toast.success(r.data.message || 'Import complete');
      setImportOpen(false); setImportPreview(null); setImportFile(null); loadCatalog();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  // ===================== Assignment loaders =====================
  const loadRefs = useCallback(async () => {
    if (!orgId) return;
    try { setClasses(await schoolApi.listClasses(orgId, selectedYearId)); } catch { /* ignore */ }
  }, [orgId, selectedYearId]);

  const loadSubjects = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try { setLoading(true); setSubjects(await schoolApi.listSubjects(orgId, filterClass || undefined, filterSection || undefined, selectedYearId)); }
    catch (e) { toast.error('Failed to load subjects'); }
    finally { setLoading(false); }
  }, [orgId, filterClass, filterSection, selectedYearId]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { loadSubjects(); }, [loadSubjects]);

  // load sections for the assignments-tab class filter
  useEffect(() => {
    if (!orgId || !filterClass) { setFilterSections([]); return; }
    schoolApi.listSections(orgId, filterClass, selectedYearId).then(setFilterSections).catch(() => setFilterSections([]));
  }, [orgId, filterClass, selectedYearId]);

  // server-side catalog subject search (only when the user types)
  useEffect(() => {
    if (!dialogOpen || form.catalog_subject_id) return;
    if (!subjSearch.trim()) { setSubjResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const data = await schoolApi.listSubjectCatalog(orgId, { search: subjSearch.trim(), limit: 20 });
        setSubjResults(data.catalog || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [orgId, subjSearch, dialogOpen, form.catalog_subject_id]);

  // server-side staff search (only when the user types)
  useEffect(() => {
    if (!dialogOpen || form.teacher_id) return;
    if (!teacherSearch.trim()) { setTeacherResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const data = await schoolApi.listStaff(orgId, { search: teacherSearch.trim(), limit: 20 });
        setTeacherResults(data.staff || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [orgId, teacherSearch, dialogOpen, form.teacher_id]);

  const teacherDisplay = (s) => `${staffName(s, lang)}${s.mobile ? ` · ${s.mobile}` : ''} · ${s.code}`;

  const loadSectionsFor = async (classId) => {
    if (!classId) { setSections([]); return; }
    try { setSections(await schoolApi.listSections(orgId, classId, selectedYearId)); } catch { setSections([]); }
  };

  const openCreate = () => {
    if (catalogTotal === 0) { toast.info(t('subjects.importFirst', 'Add subjects to the Catalog first.')); setTab('catalog'); return; }
    setEditing(null); setForm(emptyAssign); setSections([]);
    setSubjSearch(''); setSubjResults([]); setSelectedSubjLabel('');
    setTeacherSearch(''); setTeacherResults([]); setSelectedTeacherLabel('');
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...emptyAssign, ...s,
      catalog_subject_id: s.catalog_subject_id || '',
      section_id: s.section_id || '',
      hours_per_week: (s.hours_per_week ?? '') === null ? '' : (s.hours_per_week ?? ''),
      uv_group: s.uv_group || '',
      uv_order: (s.uv_order ?? '') === null ? '' : (s.uv_order ?? ''),
      max_note: (s.max_note ?? '') === null ? '' : (s.max_note ?? ''),
      coefficient: (s.coefficient ?? '') === null ? '' : (s.coefficient ?? ''),
    });
    if (s.class_id) loadSectionsFor(s.class_id); else setSections([]);
    setSubjSearch(''); setSubjResults([]);
    setSelectedSubjLabel(s.name ? `${s.name}${s.code ? ' · ' + s.code : ''}` : '');
    setTeacherSearch(''); setTeacherResults([]);
    setSelectedTeacherLabel(s.teacher_id ? (s.teacher_name || '') : '');
    setDialogOpen(true);
  };

  const onClassChange = (v) => {
    setForm((prev) => ({ ...prev, class_id: v, section_id: '' }));
    loadSectionsFor(v);
  };

  const pickSubject = (c) => {
    setForm((prev) => ({ ...prev, catalog_subject_id: c.id, name: c.name, code: prev.code || c.code || '' }));
    setSelectedSubjLabel(`${c.name}${c.code ? ' · ' + c.code : ''}`);
    setSubjResults([]); setSubjSearch('');
  };
  const clearSubject = () => {
    setForm((prev) => ({ ...prev, catalog_subject_id: '', name: '' }));
    setSelectedSubjLabel(''); setSubjSearch('');
  };

  const pickTeacher = (s) => {
    setForm((prev) => ({ ...prev, teacher_id: s.id }));
    setSelectedTeacherLabel(teacherDisplay(s));
    setTeacherResults([]); setTeacherSearch('');
  };
  const clearTeacher = () => {
    setForm((prev) => ({ ...prev, teacher_id: '' }));
    setSelectedTeacherLabel(''); setTeacherSearch('');
  };

  const save = async () => {
    if (!form.name.trim() || !form.catalog_subject_id) { toast.error(t('subjects.pickSubject', 'Please select a subject from the catalog')); return; }
    setSaving(true);
    try {
      const hours = form.hours_per_week === '' || form.hours_per_week === null ? null : Number(form.hours_per_week);
      const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      const payload = {
        organization_id: orgId, academic_year_id: selectedYearId, name: form.name, code: form.code || null,
        catalog_subject_id: form.catalog_subject_id || null,
        class_id: form.class_id || null, section_id: form.section_id || null,
        teacher_id: form.teacher_id || null,
        hours_per_week: Number.isFinite(hours) ? hours : null,
        subject_type: form.subject_type,
        uv_group: form.uv_group?.trim() || null,
        uv_order: numOrNull(form.uv_order),
        max_note: numOrNull(form.max_note),
        coefficient: numOrNull(form.coefficient),
      };
      if (editing) { await schoolApi.updateSubject(editing.id, payload); } else { await schoolApi.createSubject(payload); }
      toast.success(t('common.saved', 'Saved')); setDialogOpen(false); loadSubjects();
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => { try { await schoolApi.deleteSubject(deleteId); toast.success(t('common.deleted', 'Deleted')); setDeleteId(null); loadSubjects(); } catch (e) { toast.error('Failed'); } };

  // ----- Assignment import -----
  const onPickAssignFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAssignFile(file); setAssignPreview(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/school/subjects/assignments/import-excel/preview?organization_id=${orgId}`, fd);
      setAssignPreview(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to preview file'); setAssignFile(null); }
    if (assignFileRef.current) assignFileRef.current.value = '';
  };

  const runAssignImport = async () => {
    if (!assignFile) return;
    setAssignImporting(true);
    try {
      const fd = new FormData(); fd.append('file', assignFile);
      const r = await axios.post(`${API}/school/subjects/assignments/import-excel?organization_id=${orgId}`, fd);
      toast.success(r.data.message || 'Import complete');
      setAssignImportOpen(false); setAssignPreview(null); setAssignFile(null); loadSubjects();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setAssignImporting(false); }
  };

  // ----- Copy subjects from current section to another section (same class, no teacher) -----
  const openCopy = () => { setCopyTargetSection(''); setCopyOpen(true); };

  const runCopy = async () => {
    if (!copyTargetSection) { toast.error(t('subjects.pickTargetSection', 'Please select a target section')); return; }
    setCopying(true);
    try {
      const r = await schoolApi.copySubjectsToSection({
        organization_id: orgId,
        class_id: filterClass,
        from_section_id: filterSection,
        to_section_id: copyTargetSection,
      });
      if ((r.total || 0) === 0) {
        toast.info(t('subjects.copyNothing', 'No subjects to copy in the source section'));
      } else {
        toast.success(t('subjects.copyDone', '{copied} created, {updated} overwritten')
          .replace('{copied}', r.copied ?? 0).replace('{updated}', r.updated ?? 0));
      }
      setCopyOpen(false);
      loadSubjects();
    } catch (e) { toast.error(e?.response?.data?.detail || t('subjects.copyFailed', 'Failed to copy subjects')); }
    finally { setCopying(false); }
  };

  const filterClassName = classes.find((c) => c.id === filterClass)?.name || '';
  const filterSectionName = filterSections.find((s) => s.id === filterSection)?.name || '';
  const copyTargetOptions = filterSections.filter((s) => s.id !== filterSection);

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('subjects.title', 'Subjects')} subtitle={t('subjects.subtitle', 'Build a subject catalog, then assign subjects to classes and teachers')} icon={BookOpen} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="catalog" className="gap-2" data-testid="tab-catalog"><Library className="w-4 h-4" /> {t('subjects.catalog', 'Subject Catalog')}</TabsTrigger>
          <TabsTrigger value="assignments" className="gap-2" data-testid="tab-assignments"><ClipboardList className="w-4 h-4" /> {t('subjects.assignments', 'Class Assignments')}</TabsTrigger>
        </TabsList>

        {/* ============== CATALOG TAB ============== */}
        <TabsContent value="catalog">
          <Card className="p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder={t('subjects.searchCatalog', 'Search subjects...')} value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} className="pl-9" data-testid="catalog-search-input" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground px-1">{catalogTotal} {t('subjects.inCatalog', 'subjects')}</span>
                {canAdmin && <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="catalog-import-button"><Upload className="w-4 h-4" /> {t('common.import', 'Import')}</Button>}
                {canAdmin && <Button onClick={openCatCreate} className="gap-2" data-testid="catalog-new-button"><Plus className="w-4 h-4" /> {t('subjects.newCatalog', 'New Subject')}</Button>}
              </div>
            </div>
          </Card>

          <Card>
            {catalogLoading ? <div className="p-4"><LoadingState /></div> : catalog.length === 0 ? (
              <EmptyState icon={Library} title={t('subjects.emptyCatalog', 'No subjects in the catalog yet')}
                description={t('subjects.emptyCatalogHint', 'Import an Excel file (column: subject_name) or add subjects manually.')}
                actionLabel={canAdmin ? t('common.import', 'Import') : undefined} onAction={canAdmin ? () => setImportOpen(true) : undefined} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-3">{t('subjects.code', 'Code')}</th>
                    <th className="px-4 py-3">{t('common.name', 'Name')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions', 'Actions')}</th>
                  </tr></thead>
                  <tbody>
                    {catalog.map((c) => (
                      <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`catalog-row-${c.id}`}>
                        <td className="px-4 py-3"><Badge variant="outline" className="font-mono">{c.code || '-'}</Badge></td>
                        <td className="px-4 py-3 font-medium">{c.name}</td>
                        <td className="px-4 py-3 text-right">
                          {canAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`catalog-actions-${c.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openCatEdit(c)}><Pencil className="w-4 h-4 mr-2" /> {t('common.edit', 'Edit')}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setCatDeleteId(c.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> {t('common.delete', 'Delete')}</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ============== ASSIGNMENTS TAB ============== */}
        <TabsContent value="assignments">
          <Card className="p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={filterClass || 'all'} onValueChange={(v) => { const nv = v === 'all' ? '' : v; setFilterClass(nv); setFilterSection(''); }}>
                  <SelectTrigger className="w-full sm:w-56" data-testid="subjects-class-filter"><SelectValue placeholder={t('students.class', 'Class')} /></SelectTrigger>
                  <SelectContent><SelectItem value="all">{t('common.all', 'All')} {t('students.class', 'Class')}</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterSection || 'all'} onValueChange={(v) => setFilterSection(v === 'all' ? '' : v)} disabled={!filterClass || filterSections.length === 0}>
                  <SelectTrigger className="w-full sm:w-56" data-testid="subjects-section-filter"><SelectValue placeholder={filterClass ? (filterSections.length ? t('sections.title', 'Section') : t('sections.none', 'No sections')) : t('subjects.allSections', 'All Sections')} /></SelectTrigger>
                  <SelectContent><SelectItem value="all">{t('common.all', 'All')} {t('sections.title', 'Section')}</SelectItem>{filterSections.map((sec) => <SelectItem key={sec.id} value={sec.id}>{sec.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {canAdmin && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={openCopy}
                    disabled={!filterClass || !filterSection}
                    className="gap-2"
                    data-testid="copy-subjects-button"
                    title={(!filterClass || !filterSection) ? t('subjects.copyNeedFilter', 'Select a class and section first') : undefined}
                  >
                    <Copy className="w-4 h-4" /> {t('subjects.copyToSection', 'Copy to section')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={exportSubjectClassLists}
                    disabled={!filterClass || !filterSection || exportingSubjects}
                    className="gap-2"
                    data-testid="export-subject-class-lists-button"
                    title={(!filterClass || !filterSection) ? t('subjects.copyNeedFilter', 'Select a class and section first') : undefined}
                  >
                    <FileDown className="w-4 h-4" /> {exportingSubjects ? t('common.saving', 'Working...') : t('subjects.exportClassLists', 'Export Subject Class Lists')}
                  </Button>
                  <Button variant="outline" onClick={() => setAssignImportOpen(true)} className="gap-2" data-testid="assign-import-button"><Upload className="w-4 h-4" /> {t('common.import', 'Import')}</Button>
                  <Button onClick={openCreate} className="gap-2" data-testid="add-subject-button"><Plus className="w-4 h-4" /> {t('subjects.assign', 'Assign Subject')}</Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            {loading ? <div className="p-4"><LoadingState /></div> : subjects.length === 0 ? (
              <EmptyState icon={ClipboardList} title={t('subjects.noAssignments', 'No subject assignments yet')}
                description={t('subjects.assignHint', 'Assign a catalog subject to a class and teacher, or import from Excel.')}
                actionLabel={canAdmin ? t('subjects.assign', 'Assign Subject') : undefined} onAction={canAdmin ? openCreate : undefined} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-3">{t('common.name', 'Name')}</th>
                    <th className="px-4 py-3">{t('students.class', 'Class')}</th>
                    <th className="px-4 py-3">{t('sections.title', 'Section')}</th>
                    <th className="px-4 py-3">{t('subjects.teacher', 'Teacher')}</th>
                    <th className="px-4 py-3 text-right">{t('subjects.hoursPerWeek', 'Hrs/week')}</th>
                    <th className="px-4 py-3">{t('subjects.type', 'Type')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions', 'Actions')}</th>
                  </tr></thead>
                  <tbody>
                    {subjects.map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`subject-row-${s.id}`}>
                        <td className="px-4 py-3 font-medium">{s.name}</td>
                        <td className="px-4 py-3">{s.class_name || '-'}</td>
                        <td className="px-4 py-3">{s.section_name || '-'}</td>
                        <td className="px-4 py-3">{s.teacher_name || '-'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.hours_per_week ?? '-'}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className={s.subject_type === 'practical' ? 'text-amber-700' : 'text-muted-foreground'}>{s.subject_type === 'practical' ? t('subjects.practical', 'Practical') : t('subjects.theory', 'Theory')}</Badge></td>
                        <td className="px-4 py-3 text-right">
                          {canAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`subject-actions-${s.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(s)} data-testid={`subject-edit-${s.id}`}><Pencil className="w-4 h-4 mr-2" /> {t('common.edit', 'Edit')}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeleteId(s.id)} className="text-red-600" data-testid={`subject-delete-${s.id}`}><Trash2 className="w-4 h-4 mr-2" /> {t('common.delete', 'Delete')}</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Catalog create/edit dialog ===== */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent data-testid="catalog-dialog">
          <DialogHeader>
            <DialogTitle>{catEditing ? t('subjects.editCatalog', 'Edit Subject') : t('subjects.newCatalog', 'New Subject')}</DialogTitle>
            <DialogDescription>{t('subjects.code', 'Code')}: <span className="font-mono">{catNextCode || '—'}</span></DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div><Label>{t('common.name', 'Name')} *</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="catalog-name-input" /></div>
            <div><Label>{t('subjects.code', 'Code')}</Label><Input value={catForm.code} onChange={(e) => setCatForm({ ...catForm, code: e.target.value })} placeholder={catNextCode} data-testid="catalog-code-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button><Button onClick={saveCatalog} disabled={catSaving} data-testid="catalog-save-button">{catSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Catalog import dialog ===== */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-import-dialog">
          <DialogHeader>
            <DialogTitle>{t('subjects.importTitle', 'Import Subjects')}</DialogTitle>
            <DialogDescription>{t('subjects.importHint', 'Upload an .xlsx with a single column "subject_name". Existing subjects are skipped; new ones get an auto SUB-XXXX code.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={catFileRef} type="file" accept=".xlsx,.xls" onChange={onPickCatFile} className="hidden" id="catalog-file-input" data-testid="catalog-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => catFileRef.current?.click()} data-testid="catalog-choose-file-button">{importFile ? importFile.name : t('subjects.chooseFile', 'Choose Excel file')}</Button>
            </div>
            {importPreview && (
              <div className="space-y-2">
                <div className="flex gap-4 text-sm">
                  <span><strong>{importPreview.total_rows}</strong> {t('staff.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.new_subjects}</strong> {t('staff.toCreate', 'to create')}</span>
                  <span className="text-amber-600"><strong>{importPreview.existing_subjects}</strong> {t('subjects.toSkip', 'to skip')}</span>
                </div>
                <div className="max-h-60 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr>
                      <th className="px-2 py-1 text-left">{t('staff.action', 'Action')}</th>
                      <th className="px-2 py-1 text-left">{t('common.name', 'Name')}</th>
                    </tr></thead>
                    <tbody>
                      {importPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1"><Badge variant="outline" className={r.action === 'create' ? 'text-emerald-700' : 'text-muted-foreground'}>{r.action}</Badge></td>
                          <td className="px-2 py-1">{r.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runCatImport} disabled={!importPreview || importing} data-testid="catalog-run-import-button">{importing ? t('common.importing', 'Importing...') : t('staff.confirmImport', 'Confirm Import')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Catalog delete ===== */}
      <AlertDialog open={!!catDeleteId} onOpenChange={(o) => !o && setCatDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete', 'Delete')}?</AlertDialogTitle>
            <AlertDialogDescription>{t('subjects.deleteCatalogHint', 'This removes the subject from the catalog and unlinks it from any class assignments.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCatDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-catalog">{t('common.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ===== Assignment create/edit dialog ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="subject-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('subjects.editAssign', 'Edit Assignment') : t('subjects.assign', 'Assign Subject')}</DialogTitle>
            <DialogDescription>{t('subjects.assignDialogHint', 'Pick a subject from the catalog, then assign a class, section and Teacher/Staff member.')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>{t('subjects.subject', 'Subject')} *</Label>
              <div className="relative">
                {form.catalog_subject_id && selectedSubjLabel ? (
                  <div className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid="selected-subject">
                    <span className="font-medium">{selectedSubjLabel}</span>
                    {!editing && <Button type="button" size="sm" variant="ghost" onClick={clearSubject} data-testid="subject-change-subject">{t('common.change', 'Change')}</Button>}
                  </div>
                ) : (
                  <>
                    <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input className="pl-9" placeholder={t('subjects.searchSubject', 'Type to search subject from catalog...')} value={subjSearch} onChange={(e) => setSubjSearch(e.target.value)} data-testid="subject-catalog-search" />
                    {subjResults.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="subject-catalog-results">
                        {subjResults.map((c) => (
                          <button type="button" key={c.id} onClick={() => pickSubject(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 focus:bg-muted/60 focus-visible:outline-none" data-testid={`subject-catalog-result-${c.id}`}>
                            <span className="font-medium">{c.name}</span><span className="text-muted-foreground"> · {c.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {subjSearch.trim() && subjResults.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{t('subjects.noCatalogFound', 'No subject found. Add it to the Catalog first.')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <Label>{t('students.class', 'Class')}</Label>
              <Select value={form.class_id || ''} onValueChange={onClassChange}>
                <SelectTrigger data-testid="subject-class-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('sections.title', 'Section')}</Label>
              <Select value={form.section_id || ''} onValueChange={(v) => setForm({ ...form, section_id: v })} disabled={!form.class_id || sections.length === 0}>
                <SelectTrigger data-testid="subject-section-select"><SelectValue placeholder={form.class_id ? (sections.length ? '-' : t('sections.none', 'No sections')) : t('subjects.pickClassFirst', 'Pick class first')} /></SelectTrigger>
                <SelectContent>{sections.map((sec) => <SelectItem key={sec.id} value={sec.id}>{sec.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('subjects.teacher', 'Teacher')}</Label>
              <div className="relative">
                {form.teacher_id && selectedTeacherLabel ? (
                  <div className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid="selected-teacher">
                    <span className="font-medium truncate">{selectedTeacherLabel}</span>
                    <Button type="button" size="sm" variant="ghost" onClick={clearTeacher} data-testid="subject-change-teacher">{t('common.change', 'Change')}</Button>
                  </div>
                ) : (
                  <>
                    <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input className="pl-9" placeholder={t('subjects.searchTeacher', 'Type to search teacher/staff...')} value={teacherSearch} onChange={(e) => setTeacherSearch(e.target.value)} data-testid="subject-teacher-search" />
                    {teacherResults.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="subject-teacher-results">
                        {teacherResults.map((s) => (
                          <button type="button" key={s.id} onClick={() => pickTeacher(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 focus:bg-muted/60 focus-visible:outline-none" data-testid={`subject-teacher-result-${s.id}`}>
                            <span className="font-medium">{staffName(s, lang)}</span><span className="text-muted-foreground"> {s.mobile ? `· ${s.mobile}` : ''} · {s.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {teacherSearch.trim() && teacherResults.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{t('subjects.noTeacherFound', 'No teacher/staff found.')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t('subjects.hoursPerWeek', 'Hours per week')}</Label>
              <Input type="number" min="0" step="0.5" value={form.hours_per_week} onChange={(e) => setForm({ ...form, hours_per_week: e.target.value })} placeholder="-" data-testid="subject-hours-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('subjects.type', 'Type')}</Label>
              <Select value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v })}>
                <SelectTrigger data-testid="subject-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="theory">{t('subjects.theory', 'Theory')}</SelectItem><SelectItem value="practical">{t('subjects.practical', 'Practical')}</SelectItem></SelectContent>
              </Select>
            </div>

            {/* Bulletin / report-card structure */}
            <div className="sm:col-span-2 border-t border-border pt-3 mt-1">
              <p className="text-sm font-semibold text-foreground">{t('subjects.bulletinSettings', 'Bulletin Structure')}</p>
              <p className="text-xs text-muted-foreground">{t('subjects.bulletinHint', 'Used to group and grade this discipline on report cards (UV / Componente Curricular).')}</p>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('subjects.uvGroup', 'Value Unit / Component (U.V / C.C.)')}</Label>
              <Input value={form.uv_group} onChange={(e) => setForm({ ...form, uv_group: e.target.value })} placeholder="U.V 1 : Langues" data-testid="subject-uv-group-input" />
            </div>
            <div>
              <Label>{t('subjects.uvOrder', 'Group order')}</Label>
              <Input type="number" min="0" value={form.uv_order} onChange={(e) => setForm({ ...form, uv_order: e.target.value })} placeholder="1" data-testid="subject-uv-order-input" />
            </div>
            <div>
              <Label>{t('subjects.maxNote', 'Max note (sur)')}</Label>
              <Input type="number" min="1" step="0.5" value={form.max_note} onChange={(e) => setForm({ ...form, max_note: e.target.value })} placeholder="20" data-testid="subject-max-note-input" />
            </div>
            <div>
              <Label>{t('subjects.coefficient', 'Coefficient')}</Label>
              <Input type="number" min="0" step="0.5" value={form.coefficient} onChange={(e) => setForm({ ...form, coefficient: e.target.value })} placeholder="1" data-testid="subject-coefficient-input" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button><Button onClick={save} disabled={saving} data-testid="subject-save-button">{saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Assignment import dialog ===== */}
      <Dialog open={assignImportOpen} onOpenChange={(o) => { setAssignImportOpen(o); if (!o) { setAssignPreview(null); setAssignFile(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assign-import-dialog">
          <DialogHeader>
            <DialogTitle>{t('subjects.importAssignTitle', 'Import Class Assignments')}</DialogTitle>
            <DialogDescription>{t('subjects.importAssignHint', 'Upload an .xlsx with columns: class_name, section_name, subject_name, teacher. Match-only: classes, sections, catalog subjects and Teacher/Staff must already exist — rows referencing anything missing are skipped.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={assignFileRef} type="file" accept=".xlsx,.xls" onChange={onPickAssignFile} className="hidden" id="assign-file-input" data-testid="assign-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => assignFileRef.current?.click()} data-testid="assign-choose-file-button">{assignFile ? assignFile.name : t('subjects.chooseFile', 'Choose Excel file')}</Button>
            </div>
            {assignPreview && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span><strong>{assignPreview.total_rows}</strong> {t('staff.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{assignPreview.to_create}</strong> {t('staff.toCreate', 'to create')}</span>
                  <span className="text-blue-600"><strong>{assignPreview.duplicates}</strong> {t('subjects.duplicates', 'duplicates')}</span>
                  <span className="text-red-600"><strong>{assignPreview.skipped}</strong> {t('subjects.skipped', 'skipped')}</span>
                </div>
                <div className="max-h-72 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr>
                      <th className="px-2 py-1 text-left">{t('staff.action', 'Action')}</th>
                      <th className="px-2 py-1 text-left">{t('students.class', 'Class')}</th>
                      <th className="px-2 py-1 text-left">{t('sections.title', 'Section')}</th>
                      <th className="px-2 py-1 text-left">{t('subjects.subject', 'Subject')}</th>
                      <th className="px-2 py-1 text-left">{t('subjects.teacher', 'Teacher')}</th>
                      <th className="px-2 py-1 text-left">{t('subjects.reason', 'Reason')}</th>
                    </tr></thead>
                    <tbody>
                      {assignPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1"><Badge variant="outline" className={r.action === 'create' ? 'text-emerald-700' : r.action === 'duplicate' ? 'text-blue-700' : 'text-red-700'}>{r.action}</Badge></td>
                          <td className="px-2 py-1">{r.class_name}</td>
                          <td className="px-2 py-1">{r.section_name || '-'}</td>
                          <td className="px-2 py-1">{r.subject_name}</td>
                          <td className="px-2 py-1">{r.teacher || '-'}</td>
                          <td className="px-2 py-1 text-muted-foreground">{r.reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {assignPreview.to_create === 0 && (
                  <p className="text-xs text-amber-600">{t('subjects.nothingToImport', 'Nothing to import. Make sure Classes/Sections, Catalog subjects and Teacher/Staff are imported first.')}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignImportOpen(false)} disabled={assignImporting}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runAssignImport} disabled={!assignPreview || assignPreview.to_create === 0 || assignImporting} data-testid="assign-run-import-button">{assignImporting ? t('common.importing', 'Importing...') : t('staff.confirmImport', 'Confirm Import')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Copy subjects to another section dialog ===== */}
      <Dialog open={copyOpen} onOpenChange={(o) => { setCopyOpen(o); if (!o) setCopyTargetSection(''); }}>
        <DialogContent data-testid="copy-subjects-dialog">
          <DialogHeader>
            <DialogTitle>{t('subjects.copyToSection', 'Copy to section')}</DialogTitle>
            <DialogDescription>
              {t('subjects.copyDialogHint', 'Copies all subject parameters (without teacher) from the selected section to another section of the same class. Existing subjects in the target section are overwritten.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="copy-source-info">
              <span className="text-muted-foreground">{t('subjects.copyFrom', 'From')}: </span>
              <span className="font-medium">{filterClassName}{filterSectionName ? ` — ${filterSectionName}` : ''}</span>
            </div>
            <div>
              <Label>{t('subjects.targetSection', 'Target section')} *</Label>
              <Select value={copyTargetSection || ''} onValueChange={setCopyTargetSection}>
                <SelectTrigger data-testid="copy-target-section-select"><SelectValue placeholder={t('subjects.pickTargetSection', 'Select a target section')} /></SelectTrigger>
                <SelectContent>
                  {copyTargetOptions.length === 0
                    ? <div className="px-3 py-2 text-sm text-muted-foreground">{t('subjects.noOtherSections', 'No other sections in this class')}</div>
                    : copyTargetOptions.map((sec) => <SelectItem key={sec.id} value={sec.id}>{sec.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)} disabled={copying}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runCopy} disabled={copying || !copyTargetSection} data-testid="copy-subjects-confirm-button">{copying ? t('subjects.copying', 'Copying...') : t('subjects.copyAction', 'Copy subjects')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Assignment delete ===== */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('common.delete', 'Delete')}?</DialogTitle><DialogDescription>{t('subjects.deleteAssignHint', 'This removes the subject assignment from this class.')}</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteId(null)}>{t('common.cancel', 'Cancel')}</Button><Button className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="confirm-delete-subject">{t('common.delete', 'Delete')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
